/**
 * Kveik Yeastery (Enzyferm AS, Norway) — dried multi-strain kveik cultures
 * taken from named Norwegian farmhouses, with the lactic bacteria removed
 * but the original yeast mix left intact.
 *
 * These are not single-cell isolates. Flavour intensity scales with
 * temperature; the same pack is a quiet pale-ale yeast at 18 °C and a
 * tropical bomb at 35 °C. Temperatures are published in °C. Attenuation is
 * given only as "high", so the bands here are class-typical and marked
 * `representative` except where a retailer sheet quotes a figure.
 */

import type { Yeast } from '../../schema/yeast';

/** Kveik Yeastery dried multi-strain cultures. @see Yeast */
export const kveikYeasteryYeasts: readonly Yeast[] = [
  {
    id: 'kveik-yeastery-k1',
    lab: 'Kveik Yeastery',
    productCode: 'K.1',
    name: 'Voss',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 15, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: 13,
    sta1: false,
    description:
      'The Sigmund Gjernes Voss culture as a bacteria-free multi-strain, not a single isolate: orange, mandarin and citrus with a quieter tropical and spice tail. Fastest at 31–35 °C (about 72 hours); cooler pitches stretch out and read cleaner. Suits farmhouse ales, IPAs and sours.',
    tags: ['kveik', 'voss', 'orange', 'citrus', 'norwegian', 'multi-strain', 'fast'],
    url: 'https://www.kveikyeastery.com/yeast-strains/voss',
    provenance: 'spec-sheet',
  },
  {
    id: 'kveik-yeastery-k9',
    lab: 'Kveik Yeastery',
    productCode: 'K.9',
    name: 'Ebbegarden',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 15, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Stordal farmhouse kveik from a fruit-growing valley, which is the usual explanation for the apple-and-pear lead and tropical-citrus second. Highly attenuative and hop-friendly — the lab's pick for IPA and hazy work. Finish in under 72 hours at the hot end of the range.",
    tags: ['kveik', 'ebbegarden', 'apple', 'pear', 'tropical', 'ipa', 'norwegian', 'multi-strain'],
    url: 'https://www.kveikyeastery.com/yeast-strains/ebbegarden',
    provenance: 'spec-sheet',
  },
  {
    id: 'kveik-yeastery-k14',
    lab: 'Kveik Yeastery',
    productCode: 'K.14',
    name: 'Eitrheim',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 15, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Reidar Eitrheim's Tokheim culture: apricot and peach up front, orange and pineapple behind. A solid all-rounder for darker, malt-forward beers as well as IPA and NEIPA. Intensity drops if you ferment cool; 31–35 °C is the 72-hour sweet spot.",
    tags: ['kveik', 'eitrheim', 'apricot', 'peach', 'norwegian', 'multi-strain', 'ipa'],
    url: 'https://www.kveikyeastery.com/yeast-strains/eitrheim',
    provenance: 'spec-sheet',
  },
  {
    id: 'kveik-yeastery-k22',
    lab: 'Kveik Yeastery',
    productCode: 'K.22',
    name: 'Stalljen',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 12, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Stig Seljeset's Hornindal culture from the old horse stable (stalljen) on the farm: tropical mango and pineapple with orange-lemon in support, plus a faint acidity that keeps it from tasting candy-sweet. The widest cool range of the four, so it will also do a pseudo-lager if you hold it down.",
    tags: [
      'kveik',
      'stalljen',
      'hornindal',
      'tropical',
      'mango',
      'norwegian',
      'multi-strain',
      'pseudo-lager',
    ],
    url: 'https://www.kveikyeastery.com/yeast-strains/stalljen',
    provenance: 'spec-sheet',
  },
];
